import React, { useState, useRef, useEffect, useCallback } from 'react'
import Swal from 'sweetalert2'
import { getSession } from '../../getSession'
import { massInsertNewTaskOrAppointment } from '../../utils/lead'

const AddAppointmentForm = ({
  onClose = () => {},
  onSave = () => {},
  users = [],
  initial = {},
  selectedCount = 0,
  selectedLeadIds = [],
  hashedIds = '',
  massOpBatchId = '',
  applyOnAllLeads = false,
  registerSubmitHandler
}) => {
  const [title, setTitle] = useState(initial.title || '')
  const [description, setDescription] = useState(initial.description || '')
  const [remarks, setRemarks] = useState(initial.remarks || '')
  const [startAt, setStartAt] = useState(initial.startAt || '')
  const [endAt, setEndAt] = useState(initial.endAt || '')
  const [whereField, setWhereField] = useState(initial.where || '')
  const [address, setAddress] = useState(initial.address || '')
  const [owner, setOwner] = useState(initial.owner || '')
  const [collaborators, setCollaborators] = useState(initial.collaborators || [])
  const [schedule, setSchedule] = useState(initial.schedule || false)
  const [scheduleAt, setScheduleAt] = useState(initial.scheduleAt || '')
  const [error, setError] = useState('')

  const [showCollab, setShowCollab] = useState(false)
  const collabRef = useRef(null)
  const whereInputRef = useRef(null)
  const [latitude, setLatitude] = useState(initial.latitude || '')
  const [longitude, setLongitude] = useState(initial.longitude || '')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const userOptions = users.length ? users : [
    { id: '34594', name: 'Mohit.cheema (Mohit Cheema)' },
    { id: '34595', name: 'Manish.Singh (Surjit kaur)' },
    { id: '34596', name: 'Rachit.Kumar (naseem khan)' }
  ]

  useEffect(() => {
    const onDocClick = (e) => {
      if (collabRef.current && !collabRef.current.contains(e.target)) setShowCollab(false)
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  useEffect(() => {
    if (!whereInputRef.current) return
    const googleMaps = window.google
    if (!googleMaps || !googleMaps.maps || !googleMaps.maps.places) return
    try {
      const autocomplete = new googleMaps.maps.places.Autocomplete(whereInputRef.current, {
        types: ['regions'],
        fields: ['geometry', 'formatted_address', 'name'],
        componentRestrictions: { country: 'in' }
      })
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()
        if (place.geometry && place.geometry.location) {
          setLatitude(place.geometry.location.lat())
          setLongitude(place.geometry.location.lng())
          setWhereField(place.formatted_address || place.name || '')
        }
      })
    } catch (err) {
      console.warn('Google Maps Autocomplete error:', err?.message)
    }
  }, [])

  const toggleCollaborator = (id) => {
    setCollaborators(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const validate = () => {
    setError('')
    if (!title.trim()) { setError('Title is required'); return false }
    if (!startAt) { setError('Start Date and Time is required'); return false }
    if (!endAt) { setError('End Date and Time is required'); return false }
    if (startAt && endAt && new Date(endAt) < new Date(startAt)) { setError('End time must be after start time'); return false }
    if (schedule && !scheduleAt) { setError('Schedule date/time required when scheduling'); return false }
    return true
  }

  const handleSave = useCallback(async () => {
    if (!validate()) return
    const { userId, parentId, TokenId, token } = getSession()
    const numericUserId = Number(userId) || 0
    const numericParentId = Number(parentId) || 0
    const numericBatchId = Number(massOpBatchId) || 0
    const collaboratorIds = (Array.isArray(collaborators) ? collaborators : [])
      .map(id => String(id || '').trim())
      .filter(Boolean)
      .join(',')
    const idsFromSelection = (hashedIds || (Array.isArray(selectedLeadIds) ? selectedLeadIds.map(id => String(id)).filter(Boolean).join('/') : '') || '').trim()
    const shouldUseBatchId = !!applyOnAllLeads && numericBatchId > 0
    const idsForPayload = shouldUseBatchId ? '' : idsFromSelection

    if (!idsForPayload && !shouldUseBatchId) {
      setError('No leads selected')
      return
    }

    const requestPayload = {
      Token: TokenId || token || '',
      Message: '',
      LoggedUserId: numericUserId,
      MAC_Address: '',
      IP_Address: '',
      Details: {
        massInsertUpdateTaskaAppointment: {
          Id: 0,
          Title: title.trim(),
          Description: description || '',
          Remarks: remarks || '',
          Address: whereField || '',
          CompleteAddress: address || '',
          StartDate: startAt || null,
          EndDate: endAt || null,
          AssignedTo: Number(owner) || numericUserId,
          CreatedBy: numericUserId,
          IsActive: true,
          TaskId: 0,
          Status: 'open',
          OutcomesId: 0,
          CollabratoerUserIds: collaboratorIds,
          IsSMS: false,
          IsEmail: false,
          IsTask: false,
          IsAppointment: true,
          TaskType: '',
          LeadIds: idsForPayload,
          Ids: idsForPayload,
          Batchid: shouldUseBatchId ? numericBatchId : 0,
          Latitude: latitude ? String(latitude) : '',
          Longitude: longitude ? String(longitude) : '',
          NotifyMe: false,
          SmsSchedule: '',
          EmailSchedule: '',
          scheduledatetime: schedule ? String(scheduleAt || '') : ''
        },
        strUserID: String(numericUserId || ''),
        strParentID: String(numericParentId || ''),
        strTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || ''
      },
      BroadcastName: ''
    }

    try {
      setIsSubmitting(true)
      const response = await massInsertNewTaskOrAppointment(requestPayload)
      const status = Number(response?.Status ?? response?.status ?? 0)
      const message = response?.Message || ''
      const details = response?.Details
      const successMessage = typeof details === 'string' && details.trim()
        ? details
        : (message || 'Appointment created successfully.')

      if (status === 1) {
        Swal.fire({ icon: 'success', title: 'Success', text: successMessage })
        onSave(response)
        onClose()
        return
      }

      const errorMessage = message || (typeof details === 'string' ? details : '') || 'Failed to save appointment.'
      setError(errorMessage)
      Swal.fire({ icon: 'error', title: 'Error', text: errorMessage })
    } catch (err) {
      console.error('mass appointment save error', err)
      const errorMessage = err?.message || 'An error occurred while saving the appointment.'
      setError(errorMessage)
      Swal.fire({ icon: 'error', title: 'Error', text: errorMessage })
    } finally {
      setIsSubmitting(false)
    }
  }, [
    title,
    description,
    remarks,
    startAt,
    endAt,
    whereField,
    address,
    owner,
    collaborators,
    schedule,
    scheduleAt,
    latitude,
    longitude,
    hashedIds,
    selectedLeadIds,
    massOpBatchId,
    applyOnAllLeads,
    onClose,
    onSave
  ])

  useEffect(() => {
    if (typeof registerSubmitHandler === 'function') {
      registerSubmitHandler(() => handleSave)
      return () => registerSubmitHandler(null)
    }
  }, [registerSubmitHandler, handleSave])

  return (
    <div className="w-full max-w-2xl">
      <div className="space-y-4">
        {selectedCount > 1 && (
          <div className="text-sm text-blue-700 bg-blue-50 p-3 rounded">
            Info! You are about to perform a mass action on {selectedCount} record(s)
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className='text-[#f00]'>*</span></label>
          <input value={title} onChange={e => setTitle(e.target.value)} className="w-full border px-3 py-2 rounded" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} className="w-full border px-3 py-2 rounded h-24 resize-none" placeholder="Start Typing the details about the appointment." />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
          <textarea value={remarks} onChange={e => setRemarks(e.target.value)} className="w-full border px-3 py-2 rounded h-20 resize-none" placeholder="about the appointment..." />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date and Time <span className='text-[#f00]'>*</span></label>
          <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="w-full border px-3 py-2 rounded" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date and Time <span className='text-[#f00]'>*</span></label>
          <input type="datetime-local" value={endAt} onChange={e => setEndAt(e.target.value)} className="w-full border px-3 py-2 rounded" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Where  <span className='text-[#f00]'>*</span></label>
          <input ref={whereInputRef} value={whereField} onChange={e => setWhereField(e.target.value)} placeholder="Enter location of appointment" className="w-full border px-3 py-2 rounded" autoComplete="off" />
          {latitude && <div className="mt-1 text-xs text-gray-500">Latitude: {latitude}</div>}
          {longitude && <div className="mt-1 text-xs text-gray-500">Longitude: {longitude}</div>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Complete Address</label>
          <textarea value={address} onChange={e => setAddress(e.target.value)} className="w-full border px-3 py-2 rounded h-20 resize-none" placeholder="Please enter complete address." />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
          <select value={owner} onChange={e => setOwner(e.target.value)} className="w-full border px-3 py-2 rounded">
            <option value="">Nothing selected</option>
            {userOptions.map(u => <option key={u.id} value={u.id}>{u.id}-{u.name}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Collaborators</label>
          <div className="relative" ref={collabRef}>
            <button
              type="button"
              onClick={() => setShowCollab(s => !s)}
              className="w-full text-left border rounded px-3 py-2 flex items-center justify-between"
            >
              <div className="truncate text-sm">
                {collaborators && collaborators.length > 0
                  ? userOptions.filter(u => collaborators.includes(u.id)).map(u => u.name).join(', ')
                  : 'Nothing selected'}
              </div>
              <div className="ml-2 text-gray-500">▾</div>
            </button>

            {showCollab && (
              <div className="absolute z-20 mt-1 w-full bg-white border rounded shadow max-h-48 overflow-auto">
                {userOptions.length > 0 ? (
                  userOptions.map(u => {
                    const selected = collaborators.includes(u.id)
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => toggleCollaborator(u.id)}
                        className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-gray-100 ${selected ? 'bg-blue-50' : ''}`}
                      >
                        <div className="truncate text-sm">{u.id}-{u.name}</div>
                        {selected && <div className="text-blue-600">✓</div>}
                      </button>
                    )
                  })
                ) : (
                  <div className="text-sm text-gray-500 p-2">No users</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between">
          <label className="inline-flex items-center">
            <input type="checkbox" checked={schedule} onChange={() => setSchedule(s => !s)} className=" rounded-[3px] mr-2" />
            On Schedule Date And Time
          </label>
        </div>

        {schedule && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Add Appointment onSchedule Date and Time*</label>
            <input type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)} className="w-full border px-3 py-2 rounded" />
          </div>
        )}

        {error && <div className="text-red-600 text-sm">{error}</div>}
        {isSubmitting && <div className="text-sm text-gray-500">Saving appointment...</div>}

      </div>
    </div>
  )
}

export default AddAppointmentForm
